import type { InputHTMLAttributes, ReactNode } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  icon?: string;
  trailing?: ReactNode;
}

export default function Input({
  label,
  error,
  hint,
  icon,
  trailing,
  id,
  className = '',
  ...rest
}: InputProps) {
  const inputId = id || rest.name || `field-${Math.random().toString(36).slice(2, 8)}`;

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="mb-2 block font-medium text-[13px] text-foreground-800"
        >
          {label}
        </label>
      )}
      <div className="relative">
        {icon && (
          <span className="pointer-events-none absolute right-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className={`${icon} text-[17px] leading-none`} aria-hidden="true"></i>
          </span>
        )}
        <input
          id={inputId}
          className={[
            'w-full rounded-field border bg-white text-sm text-foreground-900 placeholder:text-foreground-400 transition-colors duration-200 focus:outline-none focus:ring-2',
            'h-12',
            icon ? 'pr-10 pl-3' : 'px-4',
            trailing ? 'pl-11' : '',
            error
              ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-100'
              : 'border-background-300 focus:border-secondary-400 focus:ring-secondary-100',
            className,
          ]
            .filter(Boolean)
            .join(' ')}
          aria-invalid={error ? true : undefined}
          {...rest}
        />
        {trailing && (
          <span className="absolute left-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center">
            {trailing}
          </span>
        )}
      </div>
      {error ? (
        <p className="mt-1.5 text-[12px] text-danger-600">{error}</p>
      ) : hint ? (
        <p className="mt-1.5 text-[12px] text-foreground-400">{hint}</p>
      ) : null}
    </div>
  );
}